import { Circle, Download } from 'lucide-react';
import React, { CSSProperties, ReactNode, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { ContentVersion, useGetRequestHistoriesQuery } from '../../../generated/graphql';
import { ticksToDate } from '../../../Utils';

type HistoryDialogProps = {
    onClose: () => void;
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    request: ContentVersion | undefined;
};

const HistoryDialog = ({ visible, onClose, request, style }: HistoryDialogProps) => {
    const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const { data, loading } = useGetRequestHistoriesQuery({
        fetchPolicy: 'no-cache',
        variables: { id: request?.id },
    });

    // Attachments are stored as raw bytes in blob storage; the URL is a pre-signed SAS GET
    // URL. Fetch it as a binary blob and download via an object URL.
    const downloadAttachment = (url: string, fileName: string) => {
        setDownloadError(null);
        setDownloadingUrl(url);
        fetch(url)
            .then((res) => {
                if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
                return res.blob();
            })
            .then((blob) => {
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                setDownloadingUrl(null);
            })
            .catch((err) => {
                console.error('[HistoryDialog] download failed:', err);
                setDownloadError(err?.message ?? 'Download failed. Please try again.');
                setDownloadingUrl(null);
            });
    };

    // Attachment names are stored with their extension already appended (they come from the
    // original File.name), so only add the extension when it isn't already present.
    const buildFileName = (name?: string | null, extension?: string | null, fallback?: string | null) => {
        const base = name ?? fallback ?? 'download';
        const ext = extension ?? '';
        return ext && base.toLowerCase().endsWith(ext.toLowerCase()) ? base : `${base}${ext}`;
    };

    const renderDownloadButton = (url: string, fileName: string) => (
        <button
            type="button"
            onClick={() => downloadAttachment(url, fileName)}
            disabled={downloadingUrl === url}
            className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded text-xs font-medium transition-colors"
            style={{
                color: 'var(--accent)',
                background: 'var(--accent-soft)',
                border: 'none',
                cursor: downloadingUrl === url ? 'default' : 'pointer',
                opacity: downloadingUrl === url ? 0.6 : 1,
            }}
        >
            <Download size={12} />
            {downloadingUrl === url ? 'Downloading…' : fileName}
        </button>
    );

    const histories = [...(data?.ContentVersion?.histories ?? [])].reverse();

    return (
        <DialogBase
            title={`Request: ${request?.name}`}
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onOkClick={onClose}
            isLoading={loading}
            style={style}
            width="560px"
        >
            <div>
                    {downloadError && (
                        <p className="text-xs mb-3" style={{ color: 'var(--danger, #dc2626)' }}>{downloadError}</p>
                    )}
                    {(request?.attachment?.downloadUrl) && (
                        <div className="pb-4 mb-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
                            <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Approved File</p>
                            {renderDownloadButton(
                                request.attachment.downloadUrl,
                                buildFileName(request?.attachment?.name, request?.attachment?.extension, request?.name),
                            )}
                        </div>
                    )}
                    {histories.length === 0
                        ? <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No history entries.</p>
                        : <ol className="flex flex-col gap-0">
                            {histories.map((h, i) => (
                                <li key={h?.createdTime ?? i} className="flex gap-3 relative">
                                    {i < histories.length - 1 && (
                                        <span className="absolute left-[11px] top-6 w-0.5 h-full" style={{ background: 'var(--border-default)' }} />
                                    )}
                                    <span className="mt-0.5 z-10 flex-shrink-0">
                                        <Circle size={22} style={{ color: 'var(--accent)' }} />
                                    </span>
                                    <div className="pb-5">
                                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{h?.name}</p>
                                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                            {h?.createdBy?.name} · {ticksToDate(h?.createdTime)}
                                        </p>
                                        {h?.message && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{h.message}</p>}
                                        {(h?.attachment?.downloadUrl || h?.attachmentDownloadUrl) && renderDownloadButton(
                                            (h?.attachment?.downloadUrl || h?.attachmentDownloadUrl) as string,
                                            buildFileName(h?.attachment?.name, h?.attachment?.extension, h?.name),
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    }
            </div>
        </DialogBase>
    );
};

export default HistoryDialog;
